import { Button, Card, Dropdown, IconWrapper } from "@caprikorn/dynamo-ui";
import { classNames } from "@caprikorn/dynamo-utils";
import { type FC, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { DevPanel } from "@components/DevPanel/DevPanel";
import { LanguageSelector } from "@components/LanguageSelector/LanguageSelector";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { IS_TEST_ENV } from "@core/core.constants";
import { WindowTerminal } from "@icons";
import { ROUTES } from "@router/router.constants";
import { isAuthenticated } from "@utils/auth.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./LoginLayout.module.css";
import type { LoginLayoutProps } from "./LoginLayout.types";

export const LoginLayout: FC<LoginLayoutProps> = ({ children }) => {
  const { pathname } = useLocation();
  const { t } = useCustomTranslation("main");
  const authenticated = isAuthenticated();
  const { showLoginIllustration } = useTenant();
  const [isDevPanelOpen, setIsDevPanelOpen] = useState(false);

  if (authenticated) return <Navigate to={ROUTES.DASHBOARD} />;

  return (
    <main
      className={classNames(
        classes.container,
        showLoginIllustration && classes.illustration,
      )}
      style={
        {
          "--login-bg-image": showLoginIllustration
            ? `url(${import.meta.env.VITE_APP_FRONT_URL_PREFIX}/${window.tenant}/images/app-login.svg)`
            : "none",
        } as React.CSSProperties
      }
    >
      {IS_TEST_ENV && (
        <div className={classes.languageSelector}>
          <LanguageSelector />
        </div>
      )}
      <IconWrapper role="presentation" size="2xl" src="companyLogo" />
      <div className={classes.content}>
        <h1 className="font-title-2xl">
          {pathname === ROUTES.LOGIN_RESET
            ? t("auth.title.resetPassword")
            : t("auth.title.welcome")}
        </h1>
        <Card className={classes.card} size="lg">
          {children}
        </Card>
      </div>
      {IS_TEST_ENV && (
        <Dropdown
          isOpen={isDevPanelOpen}
          onOpenChange={setIsDevPanelOpen}
          renderTrigger={(props) => (
            <Button
              {...props}
              className={classes.devPanel}
              startIcon={WindowTerminal}
              variant="secondary"
            >
              Dev Panel
            </Button>
          )}
        >
          <DevPanel />
        </Dropdown>
      )}
    </main>
  );
};
